import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "@/components/site/Motion";
import { Label, Section, H2, Paras, CtaButton, CtaBand, BookMeeting } from "@/components/site/Blocks";

const TOPICS = ["Technical SEO", "Google algorithm changes", "AI Overviews", "ChatGPT Search", "AEO", "GEO", "Original research"];

export const Insights = () => (
  <Section tone="subtle" testid="insights-section">
    <div className="grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Reveal><Label n="09">Insights</Label></Reveal>
        <Reveal delay={0.05}><H2 className="mt-6">Search <span className="font-serif-i text-gradient-dark">Insights</span></H2></Reveal>
      </div>
      <div className="lg:col-span-7">
        <Reveal><Paras items={[
          "Search is evolving rapidly. Our Insights explore the developments that actually matter to businesses, from technical SEO and Google algorithm changes to AI Overviews, ChatGPT Search, AEO, GEO and the changing way customers discover companies online.",
          "We also publish original research examining how industries and brands are appearing across AI-powered search.",
        ]} /></Reveal>
        <div className="mt-8 flex flex-wrap gap-2">
          {TOPICS.map((t, i) => (
            <Reveal key={t} delay={i * 0.04} y={10} className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm text-slate-800 transition-colors duration-300 hover:border-violet-600 hover:text-violet-700">{t}</Reveal>
          ))}
        </div>
        <Reveal className="mt-10"><CtaButton label="Explore Insights" variant="outline" to="/insights" testid="explore-insights-cta" /></Reveal>
      </div>
    </div>
  </Section>
);

export const Studio = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  return (
    <section ref={ref} data-testid="studio-section" className="relative bg-[#FAFAFC] px-3 py-24 sm:px-6 sm:pb-32">
      <div className="grain relative overflow-hidden rounded-[32px] bg-ink text-white">
        <div className="absolute inset-0">
          <motion.img style={{ y, scale: 1.25 }} src="/images/lines.jpg" alt="Flowing neon light trails" className="h-full w-full object-cover opacity-80" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0716] via-[#0B0716]/85 to-[#0B0716]/20" />
        </div>
        <div className="wrap relative grid min-h-[620px] items-end py-16 sm:py-24 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal><Label dark>Also from Perfect Digitals</Label></Reveal>
            <Reveal delay={0.05}><H2 dark className="mt-6 lg:text-7xl">Perfect Digitals <span className="font-serif-i text-gradient">Studio</span></H2></Reveal>
            <Reveal delay={0.1}><h3 className="mt-5 font-mono text-xs uppercase tracking-[0.25em] text-violet-200">Premium AI Video Production</h3></Reveal>
            <Reveal delay={0.15}><Paras dark className="mt-6 max-w-xl" items={[
              "Perfect Digitals Studio is our specialist AI video production division, creating premium commercial video content using the latest generation of AI production technology.",
              "From concept and scripting through to visuals, editing and sound, Studio handles the complete production process.",
            ]} /></Reveal>
            <Reveal delay={0.2} className="mt-10"><CtaButton label="Visit Perfect Digitals Studio" to="/studio" testid="visit-studio-cta" /></Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export const FinalCta = () => (
  <CtaBand
    testid="final-cta"
    title={["Search Is Evolving.", <span className="font-serif-i text-gradient">Make Sure Your Business</span>, "Remains Visible."]}
    paras={["Whether your next customer searches Google, sees an AI Overview or asks ChatGPT for a recommendation, your business needs to be part of the search journey.", "Perfect Digitals helps make that happen."]}
    sub="Book a Meeting With Our Team"
    ctas={<>
      <BookMeeting testid="final-book-meeting-cta" />
      <CtaButton label="Contact Us" variant="ghost" to="/contact" testid="final-contact-cta" />
    </>}
  />
);
